import { FC } from "preact/compat";
import { useSignal } from "@preact/signals";
import { useApp } from "~/core/store";
import { SidebarNodeProps } from "./interfaces";
import ChevronRightIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import cn from "classnames";

const getDefaultIsOpen = (
    node: SidebarNodeProps["node"],
    currentTag: string,
): boolean => {
    if (!currentTag) {
        return false;
    }

    if (node.value?.tag === currentTag) {
        return true;
    }

    return node.children.some((child) => getDefaultIsOpen(child, currentTag));
};

const SidebarNode: FC<SidebarNodeProps> = ({ node }) => {
    const {
        help: {
            admin: { editorTag, isGuide, openEditor },
        },
    } = useApp();
    const isOpenable = node.children.length > 0;
    const isOpen = useSignal(getDefaultIsOpen(node, editorTag.peek()));
    const isActive = editorTag.peek() === node.value?.tag;

    const handleClick = () => {
        if (isOpenable) {
            isOpen.value = !isOpen.value;
        } else if (node.value) {
            openEditor(node.value.tag);
            isGuide.value = false;
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                className={cn(
                    "flex w-full items-center px-2 py-1",
                    isActive ? "bg-sky-100" : "hover:bg-gray-100",
                )}
            >
                {isOpenable && (
                    <ChevronRightIcon
                        width="16"
                        height="16"
                        className={cn(
                            "mr-1 fill-current transition duration-100",
                            isOpen.value && "rotate-90",
                        )}
                    />
                )}
                <span>{node.tag}</span>
            </button>
            {isOpenable && isOpen.value && (
                <div className="pl-6">
                    {node.children.map((child) => (
                        <SidebarNode key={child.tag} node={child} />
                    ))}
                </div>
            )}
        </>
    );
};

export default SidebarNode;
